import apiSlice from '@src/redux/api';
import classNames from 'classnames';
import { graphql, useStaticQuery } from 'gatsby';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useHasMounted from '../lib/useHasMounted';
import BankIdLoader from './BankIdLoader';
import Footer from './footer';
import * as styles from './layout.module.css';
import Navigation from './navigation';
import PrimaryButton from './primaryButton';
import SEO from './seo';

const Error = ({ onDismiss }) => (
  <div className={styles.fullscreenContainer}>
    <h1>Ett fel inträffade</h1>
    <p>
      Försök gärna igen om en stund och kontakta kundtjänst om problemet
      kvarstår
    </p>
    <PrimaryButton onClick={onDismiss}>OK</PrimaryButton>
  </div>
);
Error.propTypes = {
  onDismiss: PropTypes.func,
};

const Loading = ({ children }) => (
  <div className={styles.fullscreenContainer}>{children}</div>
);
Loading.propTypes = {
  children: PropTypes.node.isRequired,
};

const Layout = ({
  children,
  path,
  seoData,
  newStyle,
  opaqueNavbar,
  whiteBg,
  contactSectionHidden,
  nonOverlayedNavbar,
  hideNavbar,
  customNavbar,
  overflowVisible,
  customFooterData,
  noRelativePos,
}) => {
  const { isAuthing, isSigning } = useSelector(state => state.api.bankId);
  const isLoading = useSelector(state => state.api.isLoading);
  const error = useSelector(state => state.api.error);
  const [contentOffset, setContentOffset] = useState(0);
  const dispatch = useDispatch();

  const hasMounted = useHasMounted();

  if (error) {
    return (
      <Error onDismiss={() => dispatch(apiSlice.actions.updateError(null))} />
    );
  }

  if (isLoading) {
    return (
      <Loading>
        <h1>Loading</h1>
      </Loading>
    );
  }

  const data = useStaticQuery(graphql`
    query NavQuery {
      contentfulFooter {
        bynkLogo {
          file {
            url
          }
        }
        linkSections {
          links {
            list {
              name
              path
            }
          }
          header
        }
      }
    }
  `);

  const showingBankIdLoader = isAuthing || isSigning;
  const mainStyle = {
    marginTop: contentOffset,
  };

  const updateHeight = h => {
    setContentOffset(h);
  };

  return (
    <div
      style={{ position: !noRelativePos ? 'relative' : undefined }}
      className={classNames({
        [styles.layoutContainer]: true,
        [styles.overflowVisible]: overflowVisible,
        hasMounted: hasMounted,
        noRelativePos: noRelativePos,
        [styles.noRelativePos]: noRelativePos,
        [styles.bankIdVisible]: showingBankIdLoader,
      })}
    >
      <SEO
        title={
          seoData && seoData.title
            ? seoData.title
            : 'Rocker - Som en bank borde fungera.'
        }
        description={
          seoData && seoData.description
            ? seoData.description
            : 'Rocker gör din vardagsekonomi enklare. Betala, spara, låna, budgetera och håll koll. När, var och hur du vill smidigt i Rocker-appen.'
        }
      />
      <header>
        <div style={{ display: 'flex', justifyContent: 'center', position: 'fixed', top: '-5rem', width: '100%', zIndex: 10000 }}>
          <a className={styles.skipToContent} href="#main-content">Gå till innehåll</a>
        </div>
        {!hideNavbar && (
        <Navigation
          path={path}
          linkContent={data.contentfulFooter.linkSections}
          newStyle={newStyle}
          opaqueBg={opaqueNavbar || showingBankIdLoader}
          nonOverlayed={nonOverlayedNavbar}
          heightCallback={updateHeight}
        />
      )}
      </header>
      {customNavbar}
      {whiteBg && <div className={styles.whiteBackground} />}
      <main className={newStyle ? styles.newStyleMain : ''} style={mainStyle}>
        {showingBankIdLoader && <BankIdLoader />}
        <div style={{ display: showingBankIdLoader ? 'none' : 'block' }}>
          {children}
        </div>
      </main>
      <Footer
        customFooterContent={customFooterData}
        newStyle={newStyle}
        showContact={!contactSectionHidden}
      />
    </div>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  path: PropTypes.string,
  seoData: PropTypes.object,
  newStyle: PropTypes.bool,
  opaqueNavbar: PropTypes.bool,
  whiteBg: PropTypes.bool,
  contactSectionHidden: PropTypes.bool,
  nonOverlayedNavbar: PropTypes.bool,
  hideNavbar: PropTypes.bool,
  overflowVisible: PropTypes.bool,
  customNavbar: PropTypes.element,
  customFooterData: PropTypes.object,
  noRelativePos: PropTypes.bool,
};

export default Layout;
